import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { TextField } from '@mui/material';
import { trackEvent } from '@services/tracking';
import { Accordion } from '@design-system/components/accordion/Accordion';
import { AccordionHeader } from '../components/AccordionHeader';
import { sectionConfigs } from '../components/sectionConfigs';
import { UpdateSignInEmailFlow } from '@client/new/features/update-sign-in-email/UpdateSignInEmailFlow';
import { StepName } from '@client/new/features/update-sign-in-email/useUpdateSignInEmailFlow.hook';

type SignInEmailSectionProps = {
  signInEmail: string;
  onRefetch: () => Promise<unknown> | void;
};

export const SignInEmailSection = ({
  signInEmail,
  onRefetch,
}: SignInEmailSectionProps) => {
  const config = sectionConfigs.signInEmail;
  const [isUpdateOpen, setIsUpdateOpen] = React.useState(false);

  const startEmailUpdateFlow = React.useCallback(() => {
    setIsUpdateOpen(true);
    trackEvent({
      type: 'Update Sign-In Email Flow Opened',
      payload: { Entrypoint: 'Settings Update Button' },
    });
  }, []);
  const closeEmailUpdateFlow = React.useCallback(
    () => setIsUpdateOpen(false),
    [],
  );
  const handleEmailUpdateCompleted = React.useCallback(() => {
    trackEvent({
      type: 'Update Sign-In Email Flow Completed',
      payload: { Entrypoint: 'Settings Update Button' },
    });
    return onRefetch();
  }, [onRefetch]);
  const handleEmailUpdateAbandoned = React.useCallback((step: StepName) => {
    trackEvent({
      type: 'Update Sign-In Email Flow Abandoned',
      payload: { Entrypoint: 'Settings Update Button', Step: step },
    });
  }, []);

  return (
    <>
      <Accordion
        defaultOpen={true}
        renderHeader={() => (
          <AccordionHeader
            icon={<FontAwesomeIcon icon={faEnvelope} />}
            iconBg={config.iconBg}
            iconColor={config.iconColor}
            title={config.title}
            description={config.description}
          />
        )}
      >
        <div className="flex items-end gap-4 w-full">
          <div className="flex-1 min-w-0">
            <span className="p-1 leading-5 text-sm font-medium">Email *</span>
            <TextField className="w-full" value={signInEmail} disabled />
          </div>
          <button
            type="button"
            onClick={startEmailUpdateFlow}
            className="flex items-center justify-center h-9 px-4 py-2 gap-2 rounded-[10px] border-0 cursor-pointer bg-[#ede5fc] text-[#350584] text-sm leading-5 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Update
          </button>
        </div>
      </Accordion>
      <UpdateSignInEmailFlow
        isOpen={isUpdateOpen}
        onClose={closeEmailUpdateFlow}
        onAbandon={handleEmailUpdateAbandoned}
        currentEmail={signInEmail}
        onSuccess={handleEmailUpdateCompleted}
      />
    </>
  );
};
